import {
  faCalendarCheck,
  faCheckCircle,
  faCheckDouble,
  faClockRotateLeft,
  faRankingStar,
  faUsersViewfinder,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { conversion_rate_data } from "../../../utils/temporary_data";

interface PipelineData {
  pipeline_id: number;
  cdd_name: string;
  client_id: number;
  role: string;
  revenue: string;
  processed_date: string;
  status: number;
  notes: string;
  projected_start_date: string;
  client_name: string;
  career_level: string;
  cdd_num: string;
  add_candidates_or_switch_roles: string;
  last_update: string;
}

interface ConversionRateDashboardProps {
  pipeline: PipelineData[];
  recruiter: string;
}

// const sample = [
//      { recruiter: "Josh", conversion_rate: 17 },
//     { recruiter: "Joshua", conversion_rate: 29 }
// ]

const ConversionRateDashboard: React.FC<ConversionRateDashboardProps> = ({
  pipeline,
  recruiter,
}) => {
  const [totalRev, setTotalRev] = useState<number>(0);
  const [totalCip, setTotalCip] = useState<number>(0);
  const [conversionRate, setConversionRate] = useState<number>(0);
  const [projectedHires, setProjectedHires] = useState<number>(0);
  const [projectedRevenue, setProjectedRevenue] = useState<number>(0);

  useEffect(() => {
    const processData = () => {
      // Total revenue
      const total_revenue = pipeline
        .filter((data) => [2, 3, 4].includes(data.status))
        .reduce(
          (sum, item) =>
            sum +
            (isNaN(parseFloat(item.revenue)) ? 0 : parseFloat(item.revenue)),
          0,
        );

      setTotalRev(total_revenue);

      // Total CIP
      const total_cip = pipeline.filter((pipe) =>
        [2, 3, 4].includes(pipe.status),
      ).length;

      setTotalCip(total_cip);

      // Calculate conversion rate
      const normalizedRecruiter = (recruiter ?? "").trim().toLowerCase();

      const process_conversion_rate = conversion_rate_data.filter(
        (rate) => rate.recruiter.trim().toLowerCase() === normalizedRecruiter,
      );

      const conversion_rate =
        process_conversion_rate.length > 0
          ? process_conversion_rate[0].conversion_rate
          : 0;

      setConversionRate(isNaN(conversion_rate) ? 0 : conversion_rate);

      console.log("Recruiter:", JSON.stringify(recruiter));
      console.log("Normalized Recruiter:", normalizedRecruiter);
      console.log("Conversion Rate:", conversion_rate);
      console.log("Process Conversion Rate:", process_conversion_rate);

      // Projected hires
      const projected_hires = Math.round(total_cip * (conversion_rate / 100));

      setProjectedHires(isNaN(projected_hires) ? 0 : projected_hires);

      // Projected revenue
      const projected_rev = Math.round(total_revenue * (conversion_rate / 100));

      setProjectedRevenue(isNaN(projected_rev) ? 0 : projected_rev);
    };

    processData();
  }, [pipeline, recruiter]);

  return (
    <div className="flex flex-col gap-2 mb-6 mt-4 px-2">
      <div className="flex items-center justify-center gap-4">
        <div className="bg-white w-full p-2 relative rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-6 justify-center items-center">
            <h2 className="text-4xl font-semibold text-gray-600">
              {pipeline.filter((pipe) => pipe.status === 2).length}
            </h2>
            <FontAwesomeIcon
              className="text-4xl absolute top-1 right-1 text-green-600"
              icon={faCalendarCheck}
            />
          </div>
          <h2 className="uppercase font-semibold text-gray-600 text-center">
            On Mid Stage
          </h2>
        </div>

        <div className="bg-white relative w-full p-2 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-6 justify-center items-center">
            <h2 className="text-4xl font-semibold text-gray-600">
              {pipeline.filter((pipe) => pipe.status === 3).length}
            </h2>
            <FontAwesomeIcon
              className="text-orange-500 text-4xl absolute top-1 right-1"
              icon={faCheckCircle}
            />
          </div>
          <h2 className="uppercase font-semibold text-gray-600 text-center">
            On Final Stage
          </h2>
        </div>

        <div className="bg-white relative w-full p-2 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-6 justify-center items-center">
            <h2 className="text-4xl font-semibold text-gray-600">
              {pipeline.filter((pipe) => pipe.status === 4).length}
            </h2>
            <FontAwesomeIcon
              className="text-4xl absolute top-1 right-1 text-yellow-500"
              icon={faClockRotateLeft}
            />
          </div>
          <h2 className="uppercase font-semibold text-gray-600 text-center">
            For Offer
          </h2>
        </div>

        <div className="bg-white w-full relative p-2 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-6 justify-center items-center">
            <h2 className="text-4xl font-semibold text-gray-600">{totalCip}</h2>
            <FontAwesomeIcon
              className="text-4xl absolute top-1 right-1 text-orange-400"
              icon={faUsersViewfinder}
            />
          </div>
          <h2 className="uppercase font-semibold text-gray-600 text-center">
            Total CIP
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <div className="bg-white relative w-full p-4 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-2 justify-center items-center">
            <h2 className="text-xl font-semibold text-gray-600">
              {parseFloat(totalRev.toString()).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </h2>
            <FontAwesomeIcon
              className="text-2xl absolute top-1 right-1 text-orange-400"
              icon={faRankingStar}
            />
          </div>
          <h2 className="uppercase font-semibold text-gray-600 text-center">
            Total CIP Revenue
          </h2>
        </div>

        <div className="bg-white w-full p-2 rounded shadow-xl flex flex-col items-center justify-center gap-2">
          <div
            className="relative flex items-center justify-center w-14 h-14 rounded-full"
            style={{
              background: `conic-gradient(#ff5a2b ${conversionRate}%, #e5e7eb ${conversionRate}%)`,
            }}
          >
            <div className="absolute text-sm w-12 h-12 rounded-full bg-white flex items-center justify-center text-gray-700 font-bold">
              {conversionRate}%
            </div>
          </div>
          <div>
            <h2 className="uppercase font-semibold text-gray-600 text-sm">
              Conversion Rate (%)
            </h2>
          </div>
        </div>

        <div className="bg-orange-400 relative w-full p-3 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-6 justify-center items-center">
            <h2 className="text-4xl font-semibold text-white">
              {projectedHires}
            </h2>
            <FontAwesomeIcon
              className="text-4xl absolute top-1 right-1 text-white"
              icon={faCheckDouble}
            />
          </div>
          <h2 className="uppercase font-semibold text-white text-center">
            Projected Hires
          </h2>
        </div>

        <div className="bg-orange-400 relative w-full p-4 rounded shadow-xl flex flex-col gap-3 justify-center ">
          <div className="flex gap-2 justify-center items-center">
            <h2 className="text-xl font-semibold text-white">
              {parseFloat(projectedRevenue.toString()).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </h2>
            <FontAwesomeIcon
              className="text-2xl absolute top-1 right-1 text-white"
              icon={faRankingStar}
            />
          </div>
          <h2 className="uppercase font-semibold text-white text-center">
            Projected CIP Revenue
          </h2>
        </div>
      </div>
    </div>
  );
};

export default ConversionRateDashboard;
